import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { api, correlacaoAtual } from '../api';
import { Painel, data } from '../comum';
import { FilaDeTrabalho } from './FilaDeTrabalho';

/**
 * O que o produto fez e o que está para fazer.
 *
 * Duas metades deliberadamente juntas: a fila mostra o trabalho **em aberto**
 * (o que vai rodar, o que falhou, o que está esperando o backoff) e a auditoria
 * mostra o que **já aconteceu**, com a correlação inteira. Separá-las em duas
 * abas obrigaria a pular entre telas para responder uma pergunta só — "essa
 * ação chegou ao fim?".
 */

interface EventoAuditoria {
  readonly id: string;
  readonly eventType: string;
  readonly tool: string;
  readonly organizationId: number;
  readonly approvalId: string | null;
  readonly actionId: string | null;
  readonly verificationId: string | null;
  readonly result: unknown;
  readonly occurredAt: string;
}

const ROTULO_EVENTO: Readonly<Record<string, string>> = {
  approval_created: 'Aprovação solicitada',
  approval_decided: 'Aprovação decidida',
  action_executed: 'Ação enviada ao KACE',
  action_failed: 'Ação recusada',
  verification_completed: 'Verificação concluída',
  script_homologado: 'Script homologado',
  script_revogado: 'Homologação revogada',
};

export function Execucoes({
  aoPerderSessao,
  podeDisparar,
}: {
  aoPerderSessao: () => void;
  podeDisparar: boolean;
}): ReactNode {
  const [itens, setItens] = useState<readonly EventoAuditoria[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async (): Promise<void> => {
    try {
      const r = await api.get<{ itens: readonly EventoAuditoria[] }>('/api/actions/audit');
      setItens(r.itens);
      setErro(null);
    } catch (e: unknown) {
      if ((e as { precisaLogin?: boolean }).precisaLogin === true) aoPerderSessao();
      else setErro(e instanceof Error ? e.message : String(e));
    }
  }, [aoPerderSessao]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  return (
    <div className="tela">
      <div className="cabecalho-tela">
        <h1>Execuções</h1>
        <p>Trabalho de fundo, ações no parque e trilha de auditoria.</p>
      </div>

      {/*
        🔴 O identificador de correlação fica visível na tela de troubleshooting.
        É o que transforma "não funcionou por volta das 14h" em uma busca — e
        `/api/rastro/:id` devolve o ciclo inteiro daquele identificador.
      */}
      {correlacaoAtual() !== null && (
        <p className="suave mono" style={{ fontSize: '0.8em' }}>
          Ao abrir chamado, informe: <strong>{correlacaoAtual()}</strong>
        </p>
      )}

      <FilaDeTrabalho aoPerderSessao={aoPerderSessao} podeDisparar={podeDisparar} />

      <Painel titulo="Trilha de auditoria" sub="Quem pediu, quem decidiu, o que o KACE respondeu.">
        {erro !== null && <p className="erro">{erro}</p>}
        {itens === null && <p className="suave">Carregando…</p>}
        {itens !== null && itens.length === 0 && (
          <p className="suave">Nenhuma execução registrada.</p>
        )}
        {(itens ?? []).map((x) => (
          <div
            key={x.id}
            style={{ borderBottom: '1px solid var(--borda)', paddingBottom: 10, marginBottom: 10 }}
          >
            <div className="linha">
              <strong>{ROTULO_EVENTO[x.eventType] ?? x.eventType}</strong>
              <span className="suave">{data(x.occurredAt)}</span>
            </div>
            <p className="suave">{x.tool} · organização {x.organizationId}</p>
            {/*
              Os identificadores ficam visíveis porque é por eles que se
              atravessa o ciclo: a mesma ação aparece em quatro eventos, e sem o
              actionId não dá para juntar os quatro.
            */}
            {(x.actionId !== null || x.verificationId !== null) && (
              <p className="suave mono" style={{ fontSize: '0.8em' }}>
                {x.approvalId !== null && `approval ${x.approvalId}`}
                {x.actionId !== null && ` · action ${x.actionId}`}
                {x.verificationId !== null && ` · verification ${x.verificationId}`}
              </p>
            )}
            {x.result != null && (
              <details>
                <summary className="suave">Resultado</summary>
                <pre className="mono">{JSON.stringify(x.result, null, 2)}</pre>
              </details>
            )}
          </div>
        ))}
      </Painel>
    </div>
  );
}
